import React, { useRef, useState } from 'react';

import Icon from 'components/DesignSystem/Icon';
import Segmented from 'components/DesignSystem/Segmented';
import SelectorChip from 'components/DesignSystem/SelectorChip';
import QueryCard from 'components/Meteorology/Shared/QueryCard';
import QueryCardHeader from 'components/Meteorology/Shared/QueryCard/Header';
import QueryCardChips from 'components/Meteorology/Shared/QueryCard/Chips';
import QueryCardChip from 'components/Meteorology/Shared/QueryCard/Chip';
import QueryCardTrigger from 'components/Meteorology/Shared/QueryCard/Trigger';
import TFDVChart from 'components/Meteorology/Fields/TrackingForecast/DetailedView/Chart';
import useChartData from 'hooks/Meteorology/Fields/TrackingForecast/DetailedView/useChartData';
import useHeatmapAwareDownload from 'hooks/Meteorology/TrackingForecast/useHeatmapAwareDownload';
import useDetailedViewMobile from 'hooks/Meteorology/Fields/TrackingForecast/DetailedView/Mobile/useDetailedViewMobile';
import { CHART_TYPE_LINE, CHART_TYPE_HEATMAP } from 'types/Meteorology/TrackingForecast';
import { SUBPAGE_NAMES } from 'types/Meteorology/Fields/TrackingForecast/DetailedView';
import { cssVar } from 'constants/theme';

import ControlPanelSheet from './ControlPanelSheet';
import FullscreenChart from './FullscreenChart';

import './index.scss';

export default function DetailedViewMobile() {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const {
    subpage,
    subpageLabel,
    subpageButtonProps,
    chartMode,
    setChartMode,
    chips,
  } = useDetailedViewMobile();
  const dvImages = useRef<HTMLDivElement>(null);
  // #2009 item 8: heatmap na tela → PDF com a tabela inteira; gráfico de linhas → imagem.
  const { data: chartOption } = useChartData();
  const { download, label: downloadLabel } = useHeatmapAwareDownload(dvImages, chartOption);
  const filename = `mapas_e_meteogramas_acompanhamento_previsao_${SUBPAGE_NAMES[subpage].split(' ').join('_').toLowerCase()}`;

  return (
    <div className="tf-dv-mobile">
      <div className="tf-dv-mobile-pills">
        {subpageButtonProps.map((buttonProps) => (
          <SelectorChip
            key={buttonProps.text}
            selected={buttonProps.active}
            onClick={buttonProps.onClick}
          >
            {buttonProps.text}
          </SelectorChip>
        ))}
      </div>

      <QueryCard>
        <QueryCardHeader label="Consulta atual" title={subpageLabel}>
          {chips.length > 0 && (
            <QueryCardChips>
              {chips.map((chip) => <QueryCardChip key={chip}>{chip}</QueryCardChip>)}
            </QueryCardChips>
          )}
        </QueryCardHeader>
        <QueryCardTrigger onClick={() => setSheetOpen(true)} />
      </QueryCard>

      <div className="tf-dv-mobile-chart-card">
        <div className="tf-dv-mobile-chart-card-header">
          <Segmented
            className="tf-dv-mobile-chart-mode-switch"
            size="sm"
            fill={false}
            ariaLabel="Tipo de gráfico"
            options={[
              { value: CHART_TYPE_LINE, icon: 'line_axis', ariaLabel: 'Gráfico de linhas' },
              { value: CHART_TYPE_HEATMAP, icon: 'table_rows', ariaLabel: 'Heatmap' },
            ]}
            value={chartMode}
            onChange={setChartMode}
          />
          <div className="tf-dv-mobile-chart-card-actions">
            <button
              type="button"
              aria-label={downloadLabel}
              className="tf-dv-mobile-chart-card-action"
              onClick={download(filename)}
            >
              <Icon iconType="download" color={cssVar('text-strong')} size="small" />
            </button>
            <button
              type="button"
              aria-label="Tela cheia"
              className="tf-dv-mobile-chart-card-action"
              onClick={() => setFullscreenOpen(true)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={cssVar('text-strong')} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3" />
              </svg>
            </button>
          </div>
        </div>
        <div ref={dvImages}>
          <TFDVChart titleMaxWidth={280} />
        </div>
      </div>

      <ControlPanelSheet open={sheetOpen} dismiss={() => setSheetOpen(false)} />
      <FullscreenChart open={fullscreenOpen} dismiss={() => setFullscreenOpen(false)} />
    </div>
  );
}
